import React, { useMemo, useState, useEffect } from "react";
import { GsdTopHeader } from "./GsdTopHeader.jsx";
import { GsdSidebarNav } from "./GsdSidebarNav.jsx";
import { GsdAppFooter } from "./GsdAppFooter.jsx";
import { resolveVisibleNavGroups, validateAppConfig, getLocaleLabel } from "../config/resolveNavigation.js";

/**
 * Main GSD platform shell — config-driven layout (GolfMAP / PokerPUR ADN).
 * @param {object} props
 * @param {import('../config/appConfigTypes.js').GsdAppConfig} props.config
 * @param {string} [props.locale]
 * @param {string} [props.activeRole]
 * @param {boolean} [props.isAuthenticated]
 * @param {string} [props.userDisplayName]
 * @param {string} [props.version]
 * @param {React.ReactNode} [props.brand]
 * @param {React.ReactNode} [props.languageToggle]
 * @param {React.ReactNode} [props.helpSlot]
 * @param {React.ReactNode} [props.trainingSlot]
 * @param {React.ReactNode} [props.loginSlot]
 * @param {() => void} [props.onLogout]
 * @param {(item: object, meta: object) => React.ReactNode} [props.renderNavLink]
 * @param {(item: object) => boolean} [props.isItemActive]
 * @param {'top-nav'|'sidebar'} [props.layoutVariant]
 * @param {'public'|'auth'|'admin'|'manager'|'player'} [props.shellMode]
 * @param {Array} [props.navGroups] — override computed nav groups
 * @param {boolean} [props.hideHeaderNav]
 * @param {React.ReactNode} [props.sidebarSlot]
 * @param {React.ReactNode} [props.primaryNavSlot]
 * @param {React.ReactNode} [props.playerTabsSlot]
 * @param {React.ReactNode} [props.userContextSlot]
 * @param {React.ReactNode} [props.roleSwitcherSlot]
 * @param {Array} [props.mobileNavItems]
 * @param {React.ReactNode} props.children
 * @param {React.ReactNode} [props.shellWrapper]
 * @param {string} [props.footerMetaLine]
 * @param {React.ReactNode} [props.footerExtra]
 * @param {string} [props.testId]
 * @param {string} [props.navCloseEvent]
 */
export function GsdAppShell({
  config,
  locale = "fr",
  activeRole = "guest",
  isAuthenticated = false,
  userDisplayName,
  version,
  brand,
  languageToggle,
  helpSlot,
  trainingSlot,
  loginSlot,
  onLogout,
  renderNavLink,
  isItemActive,
  layoutVariant = "top-nav",
  shellMode = "public",
  navGroups: navGroupsProp,
  hideHeaderNav = false,
  sidebarSlot,
  primaryNavSlot,
  playerTabsSlot,
  userContextSlot,
  roleSwitcherSlot,
  mobileNavItems,
  children,
  shellWrapper,
  footerMetaLine,
  footerExtra,
  testId = "gsd-app-shell",
  navCloseEvent,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!navCloseEvent) return undefined;
    const close = () => setMobileOpen(false);
    window.addEventListener(navCloseEvent, close);
    return () => window.removeEventListener(navCloseEvent, close);
  }, [navCloseEvent]);

  const warnings = useMemo(() => validateAppConfig(config), [config]);
  useEffect(() => {
    if (warnings.length && typeof console !== "undefined") {
      console.warn("[GsdAppShell] config warnings:", warnings);
    }
  }, [warnings]);

  const navGroups = useMemo(
    () =>
      navGroupsProp ??
      resolveVisibleNavGroups(config, { role: activeRole, isAuthenticated, locale }),
    [navGroupsProp, config, activeRole, isAuthenticated, locale],
  );

  const footerMeta =
    footerMetaLine ||
    getLocaleLabel(
      {
        labelFr: `${config.appShortName} · environnement local / démo`,
        labelEn: `${config.appShortName} · local / demo environment`,
      },
      locale,
    );

  const inner = (
    <div
      className={`gsd-app-shell gsd-app-shell--${layoutVariant} gsd-app-shell--mode-${shellMode}`}
      data-testid={testId}
      data-shell-mode={shellMode}
    >
      <GsdTopHeader
        config={config}
        locale={locale}
        activeRole={activeRole}
        isAuthenticated={isAuthenticated}
        userDisplayName={userDisplayName}
        version={version}
        brand={brand}
        languageToggle={languageToggle}
        helpSlot={helpSlot}
        trainingSlot={trainingSlot}
        loginSlot={loginSlot}
        onLogout={onLogout}
        navGroups={hideHeaderNav ? [] : navGroups}
        renderNavLink={renderNavLink}
        isItemActive={isItemActive}
        mobileNavOpen={mobileOpen}
        onMobileNavToggle={() => setMobileOpen((o) => !o)}
        layoutVariant={layoutVariant}
        hideHeaderNav={hideHeaderNav}
        userContextSlot={userContextSlot}
        roleSwitcherSlot={roleSwitcherSlot}
        mobileNavItems={mobileNavItems}
      />

      {primaryNavSlot}
      {playerTabsSlot}

      <div className="gsd-app-shell__body">
        {sidebarSlot}
        {layoutVariant === "sidebar" && !sidebarSlot ? (
          <GsdSidebarNav groups={navGroups} renderLink={renderNavLink} isItemActive={isItemActive} />
        ) : null}
        <div className="gsd-app-shell__content">
          <main className="gsd-app-shell__main" data-testid="gsd-main-content">
            {children}
          </main>
        </div>
      </div>

      <GsdAppFooter brandLine={config.appName} metaLine={footerMeta} version={version} extra={footerExtra} />
    </div>
  );

  if (shellWrapper) {
    return React.cloneElement(shellWrapper, {}, inner);
  }
  return inner;
}

/** Public-only preset — guest navigation. */
export function GsdPublicShell(props) {
  return (
    <GsdAppShell
      {...props}
      activeRole="guest"
      isAuthenticated={false}
      testId={props.testId || "gsd-public-shell"}
    />
  );
}

/** Connected role preset — requires authentication context from caller. */
export function GsdRoleShell(props) {
  return (
    <GsdAppShell
      {...props}
      isAuthenticated={props.isAuthenticated ?? true}
      testId={props.testId || "gsd-role-shell"}
    />
  );
}

/** Admin / Hanshi preset — same shell, admin nav groups filtered by config roles. */
export function GsdAdminShell(props) {
  return (
    <GsdAppShell
      {...props}
      activeRole={props.activeRole || "hanshi"}
      isAuthenticated
      testId={props.testId || "gsd-admin-shell"}
    />
  );
}
